import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { useTheme } from "../contexts/ThemeContext";
import { useViewMode } from "../contexts/ViewModeContext";

const getNavItems = (isClient) => {
  const baseItems = [
    { label: "Features", href: "#features" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Careers", href: "/careers", isExternal: false },
    { label: "FAQ", href: "#faq" },
  ];
  
  // Add Pricing link only for client view
  if (isClient) {
    baseItems.splice(2, 0, { label: "Pricing", href: "#pricing" });
  }
  
  return baseItems;
};

const CALENDLY_LINK = "https://calendly.com/pragma-cx/discovery-call";

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { viewMode } = useViewMode();
  const location = useLocation();
  const navigate = useNavigate();
  const navItems = getNavItems(viewMode === 'client');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, item) => {
    setIsOpen(false);
    if (item.href.startsWith("#")) {
      e.preventDefault();
      if (location.pathname !== "/") {
        navigate("/" + item.href);
        return;
      }
      const element = document.querySelector(item.href);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    } else if (item.href === "/careers") {
      navigate("/careers");
    }
  };

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border"
          : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="container flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <motion.div
          onClick={() => navigate("/")}
          className="flex items-center gap-3 group cursor-pointer"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Logo size={36} />
          <span className="font-mono font-bold text-foreground text-lg tracking-tight">
            PRAGMA
          </span>
        </motion.div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item, index) => {
            const isHash = item.href.startsWith("#");
            const Component = isHash ? "a" : Link;
            const props = isHash ? { href: item.href } : { to: item.href };
            
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.3 }}
              >
                <Component
                  {...props}
                  onClick={(e) => handleNavClick(e, item)}
                  className="text-muted-foreground font-medium text-sm uppercase tracking-wider hover:text-cyan-400 transition-colors relative group"
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full" />
                </Component>
              </motion.div>
            );
          })}
          
          {/* Theme Toggle */}
          <motion.button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-muted transition-colors text-foreground"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </motion.button>
        </nav>

        {/* CTA Button */}
        <motion.a
          href={CALENDLY_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:inline-flex neon-button text-xs"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="nav-cta-button"
        >
          Get Started
        </motion.a>

        {/* Mobile Menu Button & Theme Toggle */}
        <div className="flex items-center gap-2 lg:hidden">
          <motion.button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-muted transition-colors text-foreground"
            whileTap={{ scale: 0.9 }}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </motion.button>
          <motion.button
            className="text-cyan-400 p-2"
            onClick={() => setIsOpen(!isOpen)}
            whileTap={{ scale: 0.9 }}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            className="lg:hidden border-t border-border bg-background/98 backdrop-blur-lg"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container py-6 space-y-4">
              {navItems.map((item, index) => {
                const isHash = item.href.startsWith("#");
                const Component = isHash ? "a" : Link;
                const props = isHash ? { href: item.href } : { to: item.href };
                
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Component
                      {...props}
                      onClick={(e) => handleNavClick(e, item)}
                      className="block text-muted-foreground font-medium text-sm uppercase tracking-wider hover:text-cyan-400 transition-colors py-3 border-b border-border"
                    >
                      {item.label}
                    </Component>
                  </motion.div>
                );
              })}
              <motion.a
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="neon-button w-full text-center text-xs mt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Get Started
              </motion.a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
